<template>
  <v-dialog v-model="dialogModel" max-width="650" scrollable>
    <v-card class="release-notes-card">
      <v-toolbar dense flat color="primary" dark>
        <v-icon class="mr-2">mdi-bullhorn-variant-outline</v-icon>
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          Notas de Atualização
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon small @click="dialogModel = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: 70vh">
        <v-progress-linear v-if="loading" indeterminate color="primary" />

        <div v-if="!loading && notes.length === 0" class="text-center py-12">
          <v-icon large color="grey lighten-1">mdi-note-text-outline</v-icon>
          <p class="mt-3 grey--text">Nenhuma nota de atualização.</p>
        </div>

        <v-list v-else three-line class="pa-0">
          <template v-for="(note, i) in notes">
            <v-list-item
              :key="note.id_release_note"
              class="release-note-item"
              :class="{ 'unread-note': !note.is_read }"
            >
              <v-list-item-icon class="mr-3 mt-4">
                <v-icon v-if="!note.is_read" color="primary" small>
                  mdi-circle
                </v-icon>
                <v-icon v-else color="grey lighten-2" small>
                  mdi-circle-outline
                </v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <div class="d-flex align-center mb-1">
                  <v-list-item-title class="font-weight-bold text-subtitle-2">
                    {{ note.title }}
                  </v-list-item-title>
                  <v-chip
                    v-if="note.version"
                    x-small
                    outlined
                    color="primary"
                    class="ml-2"
                  >
                    v{{ note.version }}
                  </v-chip>
                </div>

                <div class="text-caption grey--text mb-2">
                  {{ formatDate(note.created_at) }}
                  <span v-if="note.created_by_name"> · {{ note.created_by_name }}</span>
                </div>

                <div
                  class="text-body-2 release-note-description"
                  v-html="formatDescription(note.description)"
                />
              </v-list-item-content>
            </v-list-item>

            <v-divider
              v-if="i < notes.length - 1"
              :key="'d-' + note.id_release_note"
            />
          </template>
        </v-list>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useHttp, useIp } from "@/composables";
import moment from "moment";

const props = defineProps({
  dialog: { type: Boolean, default: false },
});

const emit = defineEmits(["update:dialog", "notes-read"]);

const http = useHttp();
const ips = useIp();

const notes = ref([]);
const loading = ref(false);

const dialogModel = computed({
  get: () => props.dialog,
  set: (val) => emit("update:dialog", val),
});

watch(
  () => props.dialog,
  async (val) => {
    if (val) {
      await fetchNotes();
      await markUnreadAsRead();
    }
  }
);

async function fetchNotes() {
  loading.value = true;
  try {
    const res = await http.post(ips.ipUser + "release-note/list", {});
    if (res && res.data && res.data.rows) {
      notes.value = res.data.rows;
    }
  } catch (err) {
    console.error("Erro ao carregar notas:", err);
  } finally {
    loading.value = false;
  }
}

async function markUnreadAsRead() {
  const unreadIds = notes.value
    .filter((n) => !n.is_read)
    .map((n) => n.id_release_note);

  if (unreadIds.length === 0) return;

  try {
    await http.post(ips.ipUser + "release-note/mark-read", { ids: unreadIds });
    notes.value.forEach((n) => {
      if (unreadIds.includes(n.id_release_note)) {
        n.is_read = 1;
      }
    });
    emit("notes-read");
  } catch (err) {
    console.error("Erro ao marcar notas como lidas:", err);
  }
}

function formatDate(date) {
  return moment(date).format("DD/MM/YYYY [às] HH:mm");
}

function formatDescription(text) {
  if (!text) return "";
  return text
    .replace(/\n/g, "<br>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/- (.*?)(<br>|$)/g, "• $1$2");
}
</script>

<style scoped>
.release-notes-card {
  border-radius: 8px !important;
  overflow: hidden;
}

.release-note-item {
  transition: background-color 0.2s;
}

.unread-note {
  background-color: rgba(var(--v-primary-base), 0.04);
}

.release-note-description {
  white-space: pre-wrap;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.7);
}

.theme--dark .release-note-description {
  color: rgba(255, 255, 255, 0.7);
}
</style>
